import React from 'react';
import Link from '@v2/shared/components/Link';
import { Category } from '@v2/shared/lib/types';
import { Menu } from '@headlessui/react';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type Props = {
  categories?: Category[];
};

/**
 * The whole category list behind one "ALL CATEGORIES" dropdown, which is what keeps the
 * coloured strip a single row however many categories a site has.
 *
 * headless: apps/topmags/layouts/MainLayout/Navigation/Menu. MagSpot's and MagZoom's
 * legacy headers lay the same list out by counting to eight and closing a `<ul>` inside a
 * `{#loop#}` — a column count expressed as arithmetic; this asks the grid for columns and
 * lets the browser wrap. The list itself is the one the Full layout's props builder
 * already carries, so nothing here queries.
 *
 * The panel is positioned against the row the brand's Navigation puts it in, which is why
 * every one of them marks that row `relative`: without a positioned ancestor `top-full`
 * resolves against the page and the panel opens at the bottom of it.
 *
 * That row carries the coloured bar's own padding, so `top-full` is the bar's bottom edge
 * rather than the search box's, and `mt-0.5` clears it by the 2px the panel has always
 * hung below the bar. Anchoring it to the bar is what keeps that true: while the offset
 * was a fixed `top-11` it agreed with a 34px search box and nothing else, so growing the
 * box slid the bar down over the panel.
 *
 * Rendered as an empty spacer while the categories are absent, so the strip does not
 * change height once they arrive.
 */
export default function CategoryMenu({ categories }: Props) {
  const { buildUrl } = useUrlGenerator();

  if (!categories) {
    return <div className="p-3" />;
  }

  return (
    <Menu>
      <Menu.Button className="whitespace-nowrap font-bold uppercase text-white">
        All Categories <FontAwesomeIcon icon={faCaretDown} />
      </Menu.Button>
      <Menu.Items className="absolute left-0 right-0 top-full z-50 mt-0.5 grid grid-cols-2 gap-x-4 border-2 border-solid border-slate-200 bg-white p-4 md:grid-cols-4 xl:grid-cols-5">
        {categories.map((category, index) => (
          <Menu.Item key={category.id ?? index}>
            <Link href={buildUrl(category.href)} className="mb-1 block text-sm text-primary no-underline hover:underline">
              {category.name}
            </Link>
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  );
}
